import { MaterialIcons } from "@expo/vector-icons";
import type { ComponentProps } from "react";
import { StyleSheet, View } from "react-native";

import { useCustomTheme } from "../hooks/useCustomTheme";
import { Button } from "./Button";
import { ThemedText } from "./ThemedText";

type ThemedFeedbackBaseProps = {
  iconName?: ComponentProps<typeof MaterialIcons>["name"];
  title: string;
  subtitle: string;
  text?: string;
  description?: string;
  onClose?: () => void;
  closeButtonLabel?: string;
  onRetry?: () => void;
  retryButtonLabel?: string;
  type?: "error" | "info" | "success" | (string & {});
};

/**
 * Width behaviour. The default is the fixed 736px card, so the mensa and
 * school screens keep rendering exactly what they render today
 */
type ThemedFeedbackWidthProps =
  | {
      fluid: true;
      maxWidth?: number;
      compact?: boolean;
    }
  | {
      fluid?: false;
      maxWidth?: never;
      compact?: never;
    };

type ThemedFeedbackProps = ThemedFeedbackBaseProps & ThemedFeedbackWidthProps;

export type { ThemedFeedbackWidthProps };

const FIXED_WIDTH = 736;

export const ThemedFeedback = ({
  iconName,
  title,
  subtitle,
  text,
  description,
  onClose,
  closeButtonLabel,
  onRetry,
  retryButtonLabel,
  type = "error",
  fluid,
  maxWidth,
  compact,
}: ThemedFeedbackProps) => {
  const { colors, fonts } = useCustomTheme();

  const withCompact = <A, B>(base: A, override: B) =>
    compact ? [base, override] : base;

  const typeColors = {
    error: colors.danger,
    info: colors.info,
    success: colors.success,
  };

  const headlineColor =
    typeColors[type as "error" | "info" | "success"] ?? typeColors["info"];

  const alertStyle = fluid
    ? [
        styles.alert,
        styles.alertFluid,
        { maxWidth: maxWidth ?? FIXED_WIDTH },
        ...(compact ? [styles.alertCompact] : []),
      ]
    : styles.alert;

  return (
    <View style={[alertStyle, { backgroundColor: colors.background }]}>
      <View
        style={[
          withCompact(
            styles.headlineContainer,
            styles.headlineContainerCompact,
          ),
          { backgroundColor: headlineColor },
        ]}
      >
        <MaterialIcons
          name={iconName ?? "remove-circle-outline"}
          size={compact ? 40 : 56}
          color={colors.background}
        />
        <ThemedText
          style={[
            withCompact(styles.subtitleText, styles.subtitleTextCompact),
            { color: colors.background, ...fonts.regular },
          ]}
        >
          {subtitle}
        </ThemedText>
        <ThemedText
          style={[
            withCompact(styles.titleText, styles.titleTextCompact),
            { color: colors.background, ...fonts.display },
          ]}
        >
          {title}
        </ThemedText>
      </View>
      {(text || description) && (
        <View style={styles.messageWrapper}>
          <View
            style={[
              withCompact(
                styles.messageContainer,
                styles.messageContainerCompact,
              ),
              {
                borderColor: headlineColor,
              },
            ]}
          >
            {text && (
              <ThemedText
                type="title"
                style={[
                  styles.messageTitleText,
                  { color: headlineColor, ...fonts.regular },
                ]}
              >
                {text}
              </ThemedText>
            )}
            {description && (
              <ThemedText
                type="subtitle"
                style={[styles.messageDescriptionText, fonts.bold]}
              >
                {description}
              </ThemedText>
            )}
          </View>
        </View>
      )}
      {((closeButtonLabel && onClose) || (retryButtonLabel && onRetry)) && (
        <View
          style={[
            styles.buttonsContainer,
            {
              justifyContent: (closeButtonLabel && retryButtonLabel
                ? "space-between"
                : "space-around") as "space-between" | "space-around",
            },
          ]}
        >
          {closeButtonLabel && onClose && (
            <Button
              buttonText={closeButtonLabel}
              iconName="close"
              iconPosition="left"
              iconSize={compact ? 24 : undefined}
              onPress={onClose}
              buttonStyle={withCompact(
                {
                  backgroundColor: colors.background,
                  borderColor: colors.text,
                },
                styles.buttonCompact,
              )}
              iconColor={colors.text}
              buttonTextStyle={[
                withCompact(styles.buttonTextRight, styles.buttonTextCompact),
                { color: colors.text, ...fonts.bold },
              ]}
            />
          )}
          {retryButtonLabel && onRetry && (
            <Button
              buttonText={retryButtonLabel}
              iconName="refresh"
              onPress={onRetry}
              iconPosition="right"
              iconSize={compact ? 24 : undefined}
              buttonStyle={withCompact(
                {
                  backgroundColor: colors.border,
                },
                styles.buttonCompact,
              )}
              iconColor={colors.background}
              buttonTextStyle={withCompact(
                { color: colors.background, ...fonts.bold },
                styles.buttonTextCompact,
              )}
            />
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  alert: {
    width: FIXED_WIDTH,
    borderRadius: 64,
    marginHorizontal: "auto",
    padding: 32,
    flexDirection: "column",
    gap: 32,
  },
  alertFluid: {
    width: "100%",
  },
  alertCompact: {
    borderRadius: 24,
    padding: 16,
    gap: 16,
  },
  buttonsContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  headlineContainer: {
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: 32,
    paddingTop: 48,
    paddingBottom: 64,
    gap: 40,
    paddingHorizontal: 18,
  },
  headlineContainerCompact: {
    borderRadius: 16,
    paddingTop: 24,
    paddingBottom: 24,
    gap: 16,
    paddingHorizontal: 12,
  },
  messageWrapper: {
    flexGrow: 1,
  },
  messageContainer: {
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: 64,
    padding: 64,
    gap: 48,
    borderWidth: 2,
  },
  messageContainerCompact: {
    borderRadius: 24,
    padding: 24,
    gap: 16,
  },
  subtitleText: {
    fontSize: 28,
    lineHeight: 36,
    textAlign: "center",
    alignSelf: "stretch",
    paddingHorizontal: 8,
  },
  subtitleTextCompact: {
    fontSize: 16,
    lineHeight: 21,
    alignSelf: "stretch",
    paddingHorizontal: 8,
  },
  titleText: {
    fontSize: 56,
    lineHeight: 56,
    textAlign: "center",
  },
  titleTextCompact: {
    fontSize: 30,
    lineHeight: 33,
  },
  messageTitleText: {
    textAlign: "center",
  },
  messageDescriptionText: {
    textAlign: "center",
  },
  buttonTextRight: {
    textAlign: "right",
  },
  buttonCompact: {
    height: 48,
    borderRadius: 24,
    paddingHorizontal: 20,
    gap: 8,
  },
  buttonTextCompact: {
    fontSize: 16,
    lineHeight: 21,
  },
});
